import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Address, CheckoutModalProps } from "../../types/cart";
import { useShopContext } from "../../context/ShopContext";
import { Coupon, CouponDiscountDetails, DeliverySetting } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { User } from "../../types";
import api from "../../api/client";
import { useNotification } from "../../context/NotificationContext";

interface CheckoutModalWithCitiesProps extends CheckoutModalProps {
  selectedCities: Record<number, DeliverySetting | null>;
  currentCoupon: Coupon | null;
  couponDiscount: CouponDiscountDetails | null;
}

const CheckoutModal: React.FC<CheckoutModalWithCitiesProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  selectedCities = {},
  currentCoupon,
  couponDiscount,
}) => {
  const { t } = useTranslation();
  const { stores } = useShopContext();
  const { user, setUser } = useAuth();
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // 🔹 خطوات العملية
  const [step, setStep] = useState<"profile" | "paymentChoice" | "onlinePayment" | "cashOnDelivery">("paymentChoice");
  const [updating, setUpdating] = useState(false);
  const { notify } = useNotification();

  // 🧍‍♂️ Profile data
  const [profileData, setProfileData] = useState<User | null>(user);

  useEffect(() => {
    if (user) setProfileData(user);
  }, [user]);

  // 🧾 Checkout form
  const firstSelectedCity = Object.values(selectedCities).find(Boolean);
  const defaultGovernorate = firstSelectedCity
    ? `${firstSelectedCity.city?.name || ""} - ${firstSelectedCity.city?.id || ""} - ${
        firstSelectedCity.id || ""
      }`
    : "";

  const [formData, setFormData] = useState({
    governorate: defaultGovernorate,
    compound: "",
    building_or_house: "",
    mobile: "",
    latitude: 24.7136,
    longitude: 46.6753,
    payment_method: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);

  // 🧠 check if profile is complete
  useEffect(() => {
    if (user && (!user.first_name || !user.last_name || !user.email)) {
      setStep("profile");
    }
  }, [user]);

  // دالة اختيار طريقة الدفع
  const handlePaymentChoice = (choice: "online" | "cash") => {
    if (choice === "online") {
      setStep("onlinePayment");
      setFormData(prev => ({ ...prev, payment_method: "visa" }));
    } else {
      setStep("cashOnDelivery");
      setFormData(prev => ({ ...prev, payment_method: "cash_on_delivery" }));
    }
  };

  // النقاط (dots) للتوجيه
  const getCheckoutSteps = () => {
    const baseSteps = [
      { id: "payment-choice", label: t("checkout.paymentChoice"), icon: "💳" },
      { id: "selected-stores", label: t("checkout.selectedStores"), icon: "🏪" },
      { id: "delivery-address", label: t("checkout.deliveryAddress"), icon: "📍" },
    ];

    if (step === "onlinePayment") {
      baseSteps.push(
        { id: "payment-method", label: t("checkout.paymentMethod"), icon: "💳" }
      );
    }

    baseSteps.push(
      { id: "order-confirm", label: t("checkout.confirmOrder"), icon: "✅" }
    );

    return baseSteps;
  };

  const checkoutSteps = getCheckoutSteps();

  // دالة للسكرول السلس حسب الـ ID
  const scrollToSection = (sectionId: string, offset = 20, duration = 800) => {
    if (contentRef.current) {
      const section = document.getElementById(sectionId);
      if (section) {
        const sectionTop = section.offsetTop - offset;
        smoothScrollToElement(contentRef.current, sectionTop, duration);
      }
    }
  };

  // دالة للسكرول السلس
  const smoothScrollToElement = (
    element: HTMLElement | null,
    to: number,
    duration: number
  ) => {
    if (!element) return;
    
    const start = element.scrollTop;
    const change = to - start;
    const increment = 20;
    let currentTime = 0;

    const animateScroll = () => {
      currentTime += increment;
      const val = easeInOutQuad(currentTime, start, change, duration);
      element.scrollTop = val;
      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      }
    };
    animateScroll();
  };

  // دالة easing للسكرول الناعم
  const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  };

  // 🔄 تتبع السكرول لتحديد الـ active step
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      
      const scrollTop = contentRef.current.scrollTop;
      const sections = checkoutSteps.map(step => 
        document.getElementById(step.id)
      ).filter(Boolean) as HTMLElement[];
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (scrollTop >= section.offsetTop - 100) {
          setActiveStep(i);
          break;
        }
      }
    };

    if (contentRef.current && !orderConfirmed) {
      contentRef.current.addEventListener('scroll', handleScroll);
      return () => contentRef.current?.removeEventListener('scroll', handleScroll);
    }
  }, [checkoutSteps, orderConfirmed]);

  // ✅ Profile update
  const handleProfileSave = async () => {
    if (!profileData) return;

    const errs: Record<string, string> = {};
    if (!profileData.first_name) errs.first_name = t("errors.firstNameRequired");
    if (!profileData.last_name) errs.last_name = t("errors.lastNameRequired");
    if (!profileData.email) errs.email = t("errors.emailRequired");

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    try {
      setUpdating(true);
      const { data } = await api.put<User>("/auth/profile/", profileData);
      setUser(data);
      notify("success", t("profile.updatedSuccessfully"));
      setStep("paymentChoice");
      
      setTimeout(() => {
        scrollToSection("payment-choice", 0, 800);
      }, 300);
    } catch (err) {
      console.error(err);
      notify("error", t("errors.updateFailed"));
    } finally {
      setUpdating(false);
    }
  };

  // 📋 Validate checkout
  const validateCheckout = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.compound) errs.compound = t("errors.compoundRequired");
    if (!formData.building_or_house) errs.building_or_house = t("errors.buildingRequired");
    if (!formData.mobile) errs.mobile = t("errors.mobileRequired");
    
    // التحقق من صحة رقم الجوال
    
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // 🚀 Submit checkout
  const handleSubmit = async () => {
    if (!validateCheckout()) return;

    try {
      const selected = Object.values(selectedCities).find(Boolean);
      const address: Address = {
        city: selected?.city?.name || "",
        compound: formData.compound,
        building_or_house: formData.building_or_house,
        mobile: formData.mobile,
        latitude: formData.latitude,
        longitude: formData.longitude,
      };

      const payload = {
        address,
        payment_method: formData.payment_method,
        selected_cities: selectedCities,
        coupon_code: currentCoupon?.code || null,
      };

      // إرسال الطلب
      const response = await onSubmit(payload as any, formData.payment_method);
      
      // حفظ تفاصيل الطلب
      setOrderDetails(response);
      setOrderConfirmed(true);
      
      // إذا كان الدفع عند الاستلام، لا نغلق المودال مباشرة
      if (formData.payment_method !== "cash_on_delivery") {
        // للدفع أونلاين، سيتم توجيه المستخدم إلى صفحة الدفع
        // onClose سيتم استدعاؤه من onSubmit
      }
      
    } catch (err) {
      console.error("❌ Checkout error:", err);
      notify("error", t("checkout.orderFailed"));
    }
  };

  // 🧭 Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (step === "profile") {
      setProfileData((prev: any) => (prev ? { ...prev, [name]: value } : null));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // إضافة حدث عند اختيار طريقة الدفع
  const handlePaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
    setTimeout(() => {
      scrollToSection("payment-method", 20, 700);
    }, 300);
  };

  // تأكيد الطلب النهائي
  const handleFinalConfirm = () => {
    if (!formData.mobile || !/^(05|5|9665|009665)\d{8}$/.test(formData.mobile.replace(/\s/g, ''))) {
      notify("error", t("errors.validMobileRequired"));
      return;
    }
    
    notify("success", t("checkout.orderConfirmed"));
    // هنا يمكنك إضافة منطق إضافي مثل إرسال إشعار أو حفظ في قاعدة البيانات
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-modal-title"
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-all duration-500 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      
      <div 
        ref={contentRef}
        className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-lg scroll-smooth transition-all duration-500 transform"
        style={{ scrollBehavior: 'smooth' }}
      >
        {step === "profile" ? (
          <>
            {/* 🧍‍♂️ Profile Step */}
            <div className="space-y-4">
              <h2 
                id="profile-header"
                className="text-2xl font-bold text-gray-800 mb-2"
              >
                {t("profile.completeYourProfile")}
              </h2>
              
              <p className="text-gray-600 mb-6">
                {t("profile.requiredBeforeCheckout")}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    name: "first_name",
                    label: t("profile.firstName"),
                    value: profileData?.first_name || "",
                    error: errors.first_name,
                    required: true
                  },
                  {
                    name: "last_name",
                    label: t("profile.lastName"),
                    value: profileData?.last_name || "",
                    error: errors.last_name,
                    required: true
                  },
                  {
                    name: "email",
                    label: t("profile.email"),
                    value: profileData?.email || "",
                    error: errors.email,
                    required: true
                  },
                  {
                    name: "phone_number",
                    label: t("profile.phoneNumber"),
                    value: profileData?.phone_number || "",
                    error: undefined,
                    required: false
                  }
                ].map((field, index) => (
                  <div 
                    key={field.name}
                    className="space-y-2"
                  >
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type={field.name === 'email' ? 'email' : 'text'}
                      name={field.name}
                      value={field.value}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm transition-all duration-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      placeholder={`أدخل ${field.label.toLowerCase()}`}
                      aria-required={field.required}
                      aria-invalid={!!field.error}
                    />
                    {field.error && (
                      <p className="text-red-600 text-xs mt-1" role="alert">
                        {field.error}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition-all duration-300"
                  aria-label={t("common.cancel")}
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleProfileSave}
                  disabled={updating}
                  className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  aria-label={t("profile.saveAndContinue")}
                >
                  {updating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t("profile.saving")}
                    </>
                  ) : (
                    <>
                      <span>{t("profile.saveAndContinue")}</span>
                      <span className="text-lg">→</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        ) : orderConfirmed ? (
          <>
            {/* تأكيد الطلب */}
            <div className="text-center space-y-6 py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-4xl">✅</span>
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {formData.payment_method === "cash_on_delivery" 
                    ? t("checkout.orderPlacedSuccessfully") 
                    : t("checkout.proceedToPayment")}
                </h2>
                
                <p className="text-gray-600 mb-4">
                  {formData.payment_method === "cash_on_delivery" 
                    ? t("checkout.cashOrderConfirmation")
                    : t("checkout.redirectingToPayment")}
                </p>
                
                {formData.payment_method === "cash_on_delivery" && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left mb-4">
                    <h3 className="font-semibold text-blue-800 mb-2">📞 {t("checkout.contactInfo")}</h3>
                    <p className="text-blue-700 mb-1">
                      <span className="font-medium">{t("checkout.mobile")}:</span> {formData.mobile}
                    </p>
                    <p className="text-blue-700">
                      <span className="font-medium">{t("checkout.address")}:</span> {formData.compound}، {formData.building_or_house}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-center gap-3">
                {formData.payment_method === "cash_on_delivery" && (
                  <button
                    onClick={onClose}
                    className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all duration-300"
                  >
                    {t("checkout.close")}
                  </button>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* 🧾 Checkout Steps */}
            <div className="space-y-8">
              {/* Header مع الـ ID لـ SEO */}
              <div 
                id="checkout-header" 
                className="text-center"
                itemScope
                itemType="https://schema.org/CheckoutPage"
              >
                <h1 
                  id="checkout-modal-title"
                  className="text-2xl font-bold text-gray-800 mb-2"
                  itemProp="name"
                >
                  {t("checkout.title")}
                </h1>
                
                {/* خطوات التوجيه */}
                <div className="flex items-center justify-center gap-4 mt-4 mb-6">
                  {checkoutSteps.map((stepItem, index) => (
                    <React.Fragment key={stepItem.id}>
                      <button
                        onClick={() => scrollToSection(stepItem.id, 20, 800)}
                        className={`flex flex-col items-center group cursor-pointer transition-all duration-300 ${
                          activeStep >= index ? 'opacity-100' : 'opacity-60'
                        }`}
                        aria-label={`انتقل إلى ${stepItem.label}`}
                        aria-current={activeStep === index ? 'step' : undefined}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-1 transition-all duration-300 ${
                          activeStep >= index 
                            ? 'bg-primary text-white shadow-md' 
                            : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                        }`}>
                          {activeStep > index ? '✓' : index + 1}
                        </div>
                        <span className={`text-xs transition-colors ${
                          activeStep >= index ? 'text-primary font-medium' : 'text-gray-600'
                        }`}>
                          {stepItem.label}
                        </span>
                      </button>
                      
                      {index < checkoutSteps.length - 1 && (
                        <div className={`w-8 h-0.5 transition-all duration-300 ${
                          activeStep > index ? 'bg-primary' : 'bg-gray-300'
                        }`}></div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* خطوة اختيار طريقة الدفع */}
              {step === "paymentChoice" && (
                <section 
                  id="payment-choice"
                  className="space-y-6"
                >
                  <h2 className="font-semibold text-xl text-gray-800 text-center mb-4">
                    {t("checkout.choosePaymentMethod")}
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* الدفع عند الاستلام */}
                    <div 
                      className="border-2 border-gray-200 rounded-2xl p-6 cursor-pointer hover:border-green-500 hover:shadow-lg transition-all duration-300"
                      onClick={() => handlePaymentChoice("cash")}
                    >
                      <div className="text-center space-y-4">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                          <span className="text-3xl">💰</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-800 mb-2">
                            {t("payment.cash_on_delivery")}
                          </h3>
                          <p className="text-gray-600 text-sm">
                            {t("checkout.cashDescription")}
                          </p>
                        </div>
                        <div className="text-green-600 font-semibold">
                          ✓ {t("checkout.noOnlinePayment")}
                        </div>
                      </div>
                    </div>

                    {/* الدفع أونلاين */}
                    <div 
                      className="border-2 border-gray-200 rounded-2xl p-6 cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all duration-300"
                      onClick={() => handlePaymentChoice("online")}
                    >
                      <div className="text-center space-y-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                          <span className="text-3xl">💳</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-800 mb-2">
                            {t("checkout.onlinePayment")}
                          </h3>
                          <p className="text-gray-600 text-sm">
                            {t("checkout.onlineDescription")}
                          </p>
                        </div>
                        <div className="text-blue-600 font-semibold">
                          🔒 {t("checkout.securePayment")}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* 🏬 Selected Stores */}
              {(step === "onlinePayment" || step === "cashOnDelivery") && (
                <>
                  <section 
                    id="selected-stores"
                    className="space-y-4"
                    itemScope
                    itemType="https://schema.org/Offer"
                  >
                    <h2 className="font-semibold text-lg text-gray-700 flex items-center gap-2">
                      <span className="text-xl">🏪</span>
                      {t("checkout.selectedStores")}
                    </h2>
                    
                    <div className="space-y-4">
                      {Object.entries(selectedCities).map(([storeId, cityData], index) => {
                        const store = stores.find((s) => s.id === Number(storeId));
                        return (
                          <article
                            key={storeId}
                            className="border border-gray-200 rounded-xl p-4 bg-gradient-to-r from-gray-50 to-white hover:shadow-md transition-all duration-300"
                            itemScope
                            itemType="https://schema.org/Store"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 
                                  className="font-bold text-gray-800 text-lg mb-2"
                                  itemProp="name"
                                >
                                  {store?.name}
                                </h3>
                                
                                {cityData ? (
                                  <div className="space-y-2">
                                    <p className="text-gray-700 flex items-center gap-2">
                                      <span className="text-blue-500">🏙️</span>
                                      <span itemProp="areaServed">{cityData.city.name}</span>
                                    </p>
                                    <p className="text-gray-600 flex items-center gap-2">
                                      <span className="text-green-500">⏱</span>
                                      <span itemProp="deliveryLeadTime">
                                        {cityData.estimated_days} {t("checkout.days")}
                                      </span>
                                    </p>
                                    <p className="text-gray-600 flex items-center gap-2">
                                      <span className="text-yellow-500">💰</span>
                                      <span itemProp="price">
                                        {cityData.delivery_fee} {t("checkout.currency")}
                                      </span>
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-yellow-600 text-sm mt-2 flex items-center gap-2">
                                    <span>⚠️</span>
                                    {t("checkout.deliveryNotAvailable")}
                                  </p>
                                )}
                              </div>
                              <div className="text-3xl opacity-50">
                                {index === 0 ? '🛒' : '📦'}
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>

                  {/* 📍 Address */}
                  <section 
                    id="delivery-address"
                    className="space-y-4 pt-6 border-t border-gray-200"
                    itemScope
                    itemType="https://schema.org/PostalAddress"
                  >
                    <h2 className="font-semibold text-lg text-gray-700 flex items-center gap-2">
                      <span className="text-xl">📍</span>
                      {t("checkout.deliveryAddress")}
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="hidden">
                        <input
                          type="text"
                          name="governorate"
                          value={formData.governorate}
                          readOnly
                          className="w-full border border-gray-300 bg-gray-100 rounded-md px-4 py-2 text-sm"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {t("checkout.compound")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="compound"
                          value={formData.compound}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm transition-all duration-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          placeholder="أدخل اسم المجمع أو الحي"
                          aria-required="true"
                          aria-invalid={!!errors.compound}
                          itemProp="addressLocality"
                        />
                        {errors.compound && (
                          <p className="text-red-600 text-xs mt-1" role="alert">
                            {errors.compound}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {t("checkout.building")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="building_or_house"
                          value={formData.building_or_house}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm transition-all duration-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          placeholder="رقم المبنى أو المنزل"
                          aria-required="true"
                          aria-invalid={!!errors.building_or_house}
                          itemProp="streetAddress"
                        />
                        {errors.building_or_house && (
                          <p className="text-red-600 text-xs mt-1" role="alert">
                            {errors.building_or_house}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("checkout.mobile")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm transition-all duration-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        placeholder="رقم الجوال (مثال: 0501234567)"
                        aria-required="true"
                        aria-invalid={!!errors.mobile}
                        itemProp="telephone"
                      />
                      {errors.mobile && (
                        <p className="text-red-600 text-xs mt-1" role="alert">
                          {errors.mobile}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {t("checkout.mobileHint")}
                      </p>
                    </div>
                  </section>

                  {/* 💳 Payment Method (للمدفوعات أونلاين فقط) */}
                  {step === "onlinePayment" && (
                    <section 
                      id="payment-method"
                      className="space-y-4 pt-6 border-t border-gray-200"
                      itemScope
                      itemType="https://schema.org/PaymentMethod"
                    >
                      <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                        <span className="text-xl">💳</span>
                        {t("checkout.paymentMethod")}
                      </h2>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                          { 
                            value: "visa", 
                            label: t("payment.bank_transfer"), 
                            icon: "🏦",
                            description: "تحويل بنكي آمن",
                            schemaType: "https://schema.org/BankTransfer"
                          },
                          { 
                            value: "wallet", 
                            label: t("payment.wallet"), 
                            icon: "💳",
                            description: "الرصيد المتاح في المحفظة",
                            schemaType: "https://schema.org/DigitalWallet"
                          },
                        ].map(({ value, label, icon, description, schemaType }) => (
                          <label
                            key={value}
                            className={`flex flex-col items-center border p-4 rounded-xl cursor-pointer transition-all duration-300 ${
                              formData.payment_method === value 
                                ? 'border-primary bg-primary/5 shadow-md' 
                                : 'border-gray-200 hover:border-primary/30'
                            }`}
                            itemScope
                            itemType={schemaType}
                          >
                            <input
                              type="radio"
                              name="payment_method"
                              value={value}
                              checked={formData.payment_method === value}
                              onChange={handlePaymentChange}
                              className="hidden"
                              aria-label={`اختر طريقة الدفع ${label}`}
                            />
                            <div className="text-3xl mb-2" itemProp="image">
                              {icon}
                            </div>
                            <span 
                              className="font-medium text-center text-gray-800" 
                              itemProp="name"
                            >
                              {label}
                            </span>
                            <span 
                              className="text-xs text-gray-500 text-center mt-1" 
                              itemProp="description"
                            >
                              {description}
                            </span>
                            <div className={`mt-3 w-4 h-4 rounded-full border-2 transition-all duration-300 ${
                              formData.payment_method === value 
                                ? 'bg-primary border-primary' 
                                : 'border-gray-300'
                            }`} />
                          </label>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* 🔘 Buttons */}
                  <section 
                    id="order-confirm"
                    className="pt-6 border-t border-gray-200"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-sm font-medium text-gray-600">
                          {t("checkout.totalStores")}: {Object.keys(selectedCities).length}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {step === "cashOnDelivery" 
                            ? t("checkout.confirmCashOrder")
                            : t("checkout.confirmOrder")
                          }
                        </p>
                      </div>
                      
                      <div className="flex gap-3">
                        <button
                          onClick={() => setStep("paymentChoice")}
                          className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition-all duration-300"
                          aria-label={t("checkout.back")}
                        >
                          {t("checkout.back")}
                        </button>
                        <button
                          onClick={handleSubmit}
                          disabled={isLoading}
                          className={`px-6 py-3 rounded-lg text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg ${
                            step === "cashOnDelivery"
                              ? "bg-green-600 hover:bg-green-700"
                              : "bg-blue-600 hover:bg-blue-700"
                          }`}
                          aria-label={t("checkout.placeOrder")}
                          itemScope
                          itemType="https://schema.org/OrderAction"
                        >
                          {isLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span itemProp="name">{t("checkout.processing")}</span>
                            </>
                          ) : (
                            <>
                              <span itemProp="name">
                                {step === "cashOnDelivery"
                                  ? t("checkout.confirmCashOrderBtn")
                                  : t("checkout.proceedToPaymentBtn")
                                }
                              </span>
                              <span className="text-xl">
                                {step === "cashOnDelivery" ? "✅" : "💳"}
                              </span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </section>
                </>
              )}
            </div>
          </>
        )}
        
      </div>
      
    </div>
  );
};

export default CheckoutModal;